import React, { useState } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useLanguage } from '@/contexts/LanguageContext';
import { useUsage } from '@/hooks/useUsage';

/**
 * UsageWarningBanner — surfaces at 80% of the daily cost cap and
 * upgrades to a hard "blocked" state at 100%.
 *
 * Props
 *   threshold       — number 0..1 (default 0.80)
 *   onUpgradeClick  — () => void  (optional CTA when ≥80%)
 *   showWhenBlocked — boolean (default true; hide if you want a separate hard-block UI)
 */
export const UsageWarningBanner = ({
  threshold = 0.8,
  onUpgradeClick,
  showWhenBlocked = true,
  className,
}) => {
  const { language } = useLanguage();
  const isArabic = language === 'ar';
  const { usage } = useUsage({ pollMs: 60_000 });
  const [dismissed, setDismissed] = useState(false);

  if (!usage || !usage.cap_enabled) return null;
  if (dismissed) return null;
  if ((usage.percent_used || 0) < threshold) return null;
  if (!showWhenBlocked && (usage.percent_used || 0) >= 1) return null;

  const blocked = (usage.percent_used || 0) >= 1;
  const percent = Math.round((usage.percent_used || 0) * 100);
  const fmtUsd = (n) => `$${(n ?? 0).toFixed(2)}`;
  const resetsAt = usage.resets_at ? new Date(usage.resets_at) : null;

  const headlineAr = blocked
    ? `تم بلوغ حدّ الاستخدام اليومي (${fmtUsd(usage.limit_usd)})`
    : `استهلكت ${percent}% من حدّك اليومي (${fmtUsd(usage.current_usd)} من ${fmtUsd(usage.limit_usd)})`;

  const headlineEn = blocked
    ? `Daily usage limit reached (${fmtUsd(usage.limit_usd)})`
    : `You've used ${percent}% of today's limit (${fmtUsd(usage.current_usd)} of ${fmtUsd(usage.limit_usd)})`;

  const subAr = resetsAt ? `يُعاد ضبط الحدّ في ${resetsAt.toLocaleString('ar-SA')}` : '';
  const subEn = resetsAt ? `Resets at ${resetsAt.toLocaleString()}` : '';

  return (
    <div
      role="alert"
      data-testid="usage-warning-banner"
      className={cn(
        'flex items-start gap-3 rounded-xl border px-4 py-3 text-sm',
        blocked
          ? 'border-[#DC2626]/30 bg-[#FEF2F2] text-[#7F1D1D]'
          : 'border-[#D97706]/30 bg-[#FFFBEB] text-[#92400E]',
        isArabic && 'flex-row-reverse text-right font-arabic',
        className,
      )}
    >
      <AlertTriangle className={cn('w-5 h-5 mt-0.5 shrink-0', blocked ? 'text-[#DC2626]' : 'text-[#D97706]')} />
      <div className="flex-1 min-w-0">
        <p className="font-medium leading-snug">{isArabic ? headlineAr : headlineEn}</p>
        {(isArabic ? subAr : subEn) && (
          <p className="mt-0.5 text-xs opacity-80">{isArabic ? subAr : subEn}</p>
        )}
        {onUpgradeClick && (
          <button
            type="button"
            onClick={onUpgradeClick}
            className={cn(
              'mt-2 inline-flex items-center rounded-full border border-current/30 px-3 py-1 text-xs font-medium hover:bg-white/40 transition-colors',
            )}
            data-testid="usage-warning-upgrade-button"
          >
            {isArabic ? 'الترقية' : 'Upgrade'}
          </button>
        )}
      </div>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        className="text-current/60 hover:text-current shrink-0"
        aria-label={isArabic ? 'إغلاق' : 'Close'}
        data-testid="usage-warning-dismiss"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

export default UsageWarningBanner;
